package co.maytes.standupbot.dashboard.features.settings

import co.maytes.standupbot.shared.dto.integration.GithubRepoDto

enum class ReleasesRepoMode { Standups, Picked }

enum class ReleasesRepoFilter { All, Following, NotFollowing }

enum class ReleasesGroupCheck { None, Some, All }

data class ReleasesRepoItem(val fullName: String, val isPrivate: Boolean?) {
    val owner: String get() = fullName.substringBefore('/', missingDelimiterValue = "")
    val name: String get() = fullName.substringAfter('/', missingDelimiterValue = fullName)
}

data class ReleasesRepoGroup(val owner: String, val rows: List<ReleasesRepoItem>, val followed: Int, val total: Int)

data class ReleasesFilterCounts(val all: Int, val following: Int, val notFollowing: Int)

data class ReleasesGroupSlice(val group: ReleasesRepoGroup, val rows: List<ReleasesRepoItem>, val expanded: Boolean)

data class ReleasesSliceResult(val slices: List<ReleasesGroupSlice>, val hidden: Int)

data class ReleasesChipSummary(val shown: List<String>, val more: Int)

data class ReleasesHighlightPart(val text: String, val match: Boolean)

object ReleasesRepoPicker {
    const val PAGE_SIZE = 50
    const val CHIP_LIMIT = 5

    fun modeOf(trackedRepos: List<String>): ReleasesRepoMode =
        if (trackedRepos.isEmpty()) ReleasesRepoMode.Standups else ReleasesRepoMode.Picked

    fun payload(mode: ReleasesRepoMode, selected: Set<String>): List<String> = when (mode) {
        ReleasesRepoMode.Standups -> emptyList()
        ReleasesRepoMode.Picked -> selected.sorted()
    }

    fun items(available: List<GithubRepoDto>, savedSelection: Collection<String>): List<ReleasesRepoItem> {
        val known = available.map { it.fullName }.toSet()
        val listed = available.map { ReleasesRepoItem(it.fullName, it.private) }
        val orphaned = savedSelection.filter { it !in known }.distinct().map { ReleasesRepoItem(it, null) }
        return listed + orphaned
    }

    fun matches(item: ReleasesRepoItem, query: String): Boolean {
        val needle = query.trim()
        return needle.isEmpty() || item.fullName.contains(needle, ignoreCase = true)
    }

    fun visible(
        items: List<ReleasesRepoItem>,
        selected: Set<String>,
        query: String,
        filter: ReleasesRepoFilter,
    ): List<ReleasesRepoItem> = items.filter { item ->
        matches(item, query) && when (filter) {
            ReleasesRepoFilter.All -> true
            ReleasesRepoFilter.Following -> item.fullName in selected
            ReleasesRepoFilter.NotFollowing -> item.fullName !in selected
        }
    }

    fun counts(items: List<ReleasesRepoItem>, selected: Set<String>, query: String): ReleasesFilterCounts {
        val matching = items.filter { matches(it, query) }
        val following = matching.count { it.fullName in selected }
        return ReleasesFilterCounts(all = matching.size, following = following, notFollowing = matching.size - following)
    }

    fun groups(
        visible: List<ReleasesRepoItem>,
        allItems: List<ReleasesRepoItem>,
        selected: Set<String>,
    ): List<ReleasesRepoGroup> {
        val byOwner = allItems.groupBy { it.owner }
        return visible.groupBy { it.owner }
            .map { (owner, rows) ->
                val everyone = byOwner.getValue(owner)
                ReleasesRepoGroup(
                    owner = owner,
                    rows = rows.sortedWith(
                        compareBy<ReleasesRepoItem> { it.fullName !in selected }.thenBy { it.name.lowercase() },
                    ),
                    followed = everyone.count { it.fullName in selected },
                    total = everyone.size,
                )
            }
            .sortedBy { it.owner.lowercase() }
    }

    fun check(group: ReleasesRepoGroup, selected: Set<String>): ReleasesGroupCheck {
        val picked = group.rows.count { it.fullName in selected }
        return when {
            picked == 0 -> ReleasesGroupCheck.None
            picked == group.rows.size -> ReleasesGroupCheck.All
            else -> ReleasesGroupCheck.Some
        }
    }

    fun toggleRepo(selected: Set<String>, fullName: String, checked: Boolean): Set<String> =
        if (checked) selected + fullName else selected - fullName

    fun selectAll(selected: Set<String>, rows: List<ReleasesRepoItem>): Set<String> = selected + rows.map { it.fullName }

    fun clear(selected: Set<String>, rows: List<ReleasesRepoItem>): Set<String> = selected - rows.map { it.fullName }.toSet()

    fun toggleGroup(selected: Set<String>, group: ReleasesRepoGroup): Set<String> =
        if (check(group, selected) == ReleasesGroupCheck.All) clear(selected, group.rows) else selectAll(selected, group.rows)

    fun chips(selected: Set<String>, limit: Int = CHIP_LIMIT): ReleasesChipSummary {
        val sorted = selected.sorted()
        return ReleasesChipSummary(shown = sorted.take(limit), more = (sorted.size - limit).coerceAtLeast(0))
    }

    fun slice(groups: List<ReleasesRepoGroup>, collapsed: Set<String>, limit: Int, forceExpanded: Boolean): ReleasesSliceResult {
        var budget = limit
        var hidden = 0
        val slices = groups.map { group ->
            val expanded = forceExpanded || group.owner !in collapsed
            if (!expanded) {
                ReleasesGroupSlice(group, emptyList(), expanded = false)
            } else {
                val shown = group.rows.take(budget.coerceAtLeast(0))
                budget -= shown.size
                hidden += group.rows.size - shown.size
                ReleasesGroupSlice(group, shown, expanded = true)
            }
        }
        return ReleasesSliceResult(slices, hidden)
    }

    fun highlight(fullName: String, query: String): Pair<List<ReleasesHighlightPart>, List<ReleasesHighlightPart>> {
        val needle = query.trim()
        val start = if (needle.isEmpty()) -1 else fullName.indexOf(needle, ignoreCase = true)
        val range = if (start < 0) null else start until start + needle.length
        val split = fullName.indexOf('/') + 1
        return parts(fullName.substring(0, split), 0, range) to parts(fullName.substring(split), split, range)
    }

    private fun parts(text: String, offset: Int, range: IntRange?): List<ReleasesHighlightPart> {
        if (text.isEmpty()) return emptyList()
        if (range == null) return listOf(ReleasesHighlightPart(text, false))
        val from = (range.first - offset).coerceIn(0, text.length)
        val to = (range.last + 1 - offset).coerceIn(0, text.length)
        if (from >= to) return listOf(ReleasesHighlightPart(text, false))
        return listOf(
            ReleasesHighlightPart(text.substring(0, from), false),
            ReleasesHighlightPart(text.substring(from, to), true),
            ReleasesHighlightPart(text.substring(to), false),
        ).filter { it.text.isNotEmpty() }
    }
}

object ReleasesLabels {
    val SUGGESTIONS: List<String> = listOf("security", "no-feed")

    fun add(current: List<String>, raw: String): List<String> = (current + parseHoldBackLabels(raw)).distinct()

    fun remove(current: List<String>, label: String): List<String> = current - label

    fun removeLast(current: List<String>): List<String> = current.dropLast(1)

    fun suggestions(current: List<String>): List<String> = SUGGESTIONS.filter { it !in current }

    fun effective(committed: List<String>, draft: String): List<String> = add(committed, draft)

    fun consume(current: List<String>, draft: String): Pair<List<String>, String> {
        val cut = draft.lastIndexOf(',')
        return if (cut < 0) current to draft else add(current, draft.substring(0, cut)) to draft.substring(cut + 1)
    }
}
